/** Platform Runner public state. Credentials never occur in these user-facing schemas. */
import { Schema } from "effect"
import { ProfileId, UserId } from "./ids"
import { ProfileProjectIds, ProfileScope, SubscriptionLabel } from "./runner"

export const PlatformAgent = Schema.Literals(["claude", "codex"])
export type PlatformAgent = typeof PlatformAgent.Type
export const PlatformSubscription = Schema.Struct({
  id: ProfileId,
  agent: PlatformAgent,
  createdAt: Schema.NullOr(Schema.Number),
  savedAt: Schema.Number,
  expiresAt: Schema.NullOr(Schema.Number),
  expiryEstimated: Schema.Boolean,
  validatedAt: Schema.NullOr(Schema.Number),
  quotaAccountKey: Schema.optionalKey(Schema.String),
  quotaAccountSource: Schema.optionalKey(Schema.Literals(["owner", "native"])),
  credentialGeneration: Schema.optionalKey(
    Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  ),
  /**
   * Decision 0128: the person whose subscription it is (who connected it); only they change or disconnect it. Absent on
   * subscriptions saved before 0128: the person who set the Platform Runner up.
   */
  ownerUserId: Schema.optionalKey(UserId),
  /**
   * Decision 0128: `personal` (only its owner's work, the default) or `team` (anyone's). Absent = personal. Decision
   * 0166 §3.1: `projects` = anyone's work in `projectIds` only.
   */
  scope: Schema.optionalKey(ProfileScope),
  /** Decision 0166 §3.1: the projects a `projects` scope shares it in (absent or ignored for other scopes). */
  projectIds: Schema.optionalKey(ProfileProjectIds),
  /** Decision 0166 §3.3: the owner's name for it; absent = the plan, or the provider when the plan is unknown. */
  label: Schema.optionalKey(SubscriptionLabel),
  /**
   * Decision 0166 §3.4: the subscription's account, shown unmasked to every member of the workspace. Codex: the
   * `id_token` email at connect (`provider`); Claude: typed by its owner (`owner`) until a sandbox reports it.
   */
  accountEmail: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(320))),
  accountEmailSource: Schema.optionalKey(Schema.Literals(["provider", "owner"])),
  /** Decision 0128: the plan as observed ("max", "pro", "plus"); a view field, never stored. */
  plan: Schema.optionalKey(Schema.String),
  /**
   * Decision 0128: true on another person's subscription as a member sees it: name, provider, plan, scope and owner
   * only (no account key, no credential generation).
   */
  redacted: Schema.optionalKey(Schema.Boolean),
})
export type PlatformSubscription = typeof PlatformSubscription.Type
export const PlatformConnectionFailure = Schema.Literals([
  "invalid_code",
  "provider_error",
  "account_on_hold",
  "cli_error",
  "transport_error",
  "timeout",
])
export const PlatformConnection = Schema.Struct({
  id: Schema.String,
  profileId: ProfileId,
  agent: PlatformAgent,
  state: Schema.Literals(["starting", "authorize", "connected", "failed", "expired"]),
  verificationUrl: Schema.NullOr(Schema.String),
  userCode: Schema.NullOr(Schema.String),
  expiresAt: Schema.Number,
  runnerId: Schema.optionalKey(Schema.String),
  manualVerificationUrl: Schema.optionalKey(Schema.String),
  inputSubmitted: Schema.optionalKey(Schema.Boolean),
  failureReason: Schema.optionalKey(PlatformConnectionFailure),
  providerStatus: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 400, maximum: 599 }))),
})
export type PlatformConnection = typeof PlatformConnection.Type
/** Private runner-to-platform result; never included in runner manifests or public connection state. */
export const PlatformSubscriptionProgress = Schema.TaggedUnion({
  InputAccepted: { inputId: Schema.String },
  Authorize: {
    verificationUrl: Schema.String.check(Schema.isMaxLength(8192)),
    manualVerificationUrl: Schema.optionalKey(Schema.String.check(Schema.isMaxLength(8192))),
  },
  Connected: {
    token: Schema.Redacted(Schema.String.check(Schema.isPattern(/^sk-ant-oat01-[A-Za-z0-9_-]{16,16300}$/))),
  },
  Failed: {
    reason: Schema.optionalKey(PlatformConnectionFailure),
    providerStatus: Schema.optionalKey(Schema.Int.check(Schema.isBetween({ minimum: 400, maximum: 599 }))),
  },
})
export type PlatformSubscriptionProgress = typeof PlatformSubscriptionProgress.Type
export const PlatformContainerSize = Schema.Literals(["standard-1", "standard-2", "standard-3", "standard-4"])
export const PlatformExecutionProfile = Schema.Struct({
  id: ProfileId,
  subscriptionId: ProfileId,
  agent: PlatformAgent,
  name: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  containerSize: PlatformContainerSize,
  capacity: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
  revision: Schema.Number.check(Schema.isInt(), Schema.isGreaterThanOrEqualTo(1)),
})
export type PlatformExecutionProfile = typeof PlatformExecutionProfile.Type
export const PlatformRuntime = Schema.Struct({
  tools: Schema.optionalKey(
    Schema.Array(Schema.Struct({ name: Schema.String, version: Schema.optionalKey(Schema.String) })),
  ),
  minga: Schema.String,
  bun: Schema.String,
  codex: Schema.NullOr(Schema.String),
  claude: Schema.NullOr(Schema.String),
  image: Schema.optionalKey(Schema.String),
})
export type PlatformRuntime = typeof PlatformRuntime.Type
export const PlatformGithubCredential = Schema.Struct({
  token: Schema.String.check(Schema.isMinLength(1)),
  expiresAt: Schema.NullOr(Schema.Number.check(Schema.isFinite(), Schema.isGreaterThan(0))),
})
export type PlatformGithubCredential = typeof PlatformGithubCredential.Type
export const PlatformSandboxView = Schema.Struct({
  runtime: Schema.optionalKey(PlatformRuntime),
  projectId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  runRef: Schema.optionalKey(Schema.NullOr(Schema.String)),
  model: Schema.optionalKey(Schema.NullOr(Schema.String)),
  scope: Schema.String,
  runId: Schema.String,
  profileId: ProfileId,
  subscriptionId: ProfileId,
  profileRevision: Schema.Number,
  containerSize: PlatformContainerSize,
  status: Schema.Literals(["starting", "running", "saved", "error"]),
  checkpointAt: Schema.NullOr(Schema.Number),
})
export const PlatformCodexTokens = Schema.Struct({
  accessToken: Schema.String.check(Schema.isMinLength(1)),
  chatgptAccountId: Schema.String.check(Schema.isMinLength(1)),
  chatgptPlanType: Schema.NullOr(Schema.String),
  expiresAt: Schema.Number.check(Schema.isFinite(), Schema.isGreaterThan(0)),
})
export const PlatformRunnerState = Schema.Struct({
  runtime: Schema.optionalKey(PlatformRuntime),
  configured: Schema.Boolean,
  runnerId: Schema.NullOr(Schema.String),
  /**
   * The person who set it up (decision 0128: a technical anchor for the hub; the Platform Runner is the team's and is
   * configured by workspace owners and admins). Null before the first setup.
   */
  ownerUserId: Schema.NullOr(Schema.String),
  /** Decision 0128: the viewer is a workspace owner or admin (sets it up and configures profiles, GitHub, settings). */
  manages: Schema.optionalKey(Schema.Boolean),
  status: Schema.Literals(["not_configured", "stopped", "starting", "running", "error"]),
  checkpointAt: Schema.NullOr(Schema.Number),
  githubCredentialSaved: Schema.Boolean,
  githubApp: Schema.optionalKey(Schema.Struct({ connected: Schema.Boolean, ready: Schema.Boolean })),
  subscriptions: Schema.Array(PlatformSubscription),
  connections: Schema.Array(PlatformConnection),
  profiles: Schema.optionalKey(Schema.Array(PlatformExecutionProfile)),
  sandboxes: Schema.optionalKey(Schema.Array(PlatformSandboxView)),
})
export type PlatformRunnerState = typeof PlatformRunnerState.Type
